/**
 * V7 final taxonomy for wheere-inside (Wheere staff console).
 *
 * Wheere-inside is reserved for Wheere staff: only PLATFORM_ADMIN (full access) and
 * PLATFORM_SUPPORT (read-only across every org) can authenticate. Org-scoped roles
 * (ORG_ADMIN / ORG_OPERATOR / ORG_VIEWER / ORG_CUSTOMER) live in wheere-integrator and
 * wheere-customer; they're rejected by the route guards here.
 *
 * Deleted (V7 cleanup):
 * - SUPERADMIN, WHEERE_MAINTAINER — legacy bypass aliases of PLATFORM_ADMIN, removed from KC.
 * - GATEWAY_*, TRACKER_*, SITE_*, USER_*, ADMIN, MAINTAINER, READER — V3 plat roles, replaced
 *   by the PLATFORM_/ORG_ family in V4. The legacy bundles (GATEWAY_ROLES, DEVICE_ROLES, …)
 *   that the inside topnav still references now expand to {PLATFORM_ADMIN, PLATFORM_SUPPORT}
 *   so navigation works without rewriting every callsite.
 */
export type Role = 'PLATFORM_ADMIN' | 'PLATFORM_SUPPORT' | 'ORG_ADMIN' | 'ORG_OPERATOR' | 'ORG_VIEWER' | 'ORG_CUSTOMER';

/**
 * Single canonical bundle for "who can access wheere-inside". Every domain (gateways/trackers/
 * sites/users) expands to the same set because inside is Wheere-staff territory — finer-grained
 * gating happens at the button level via {@code isXxxAdmin} signals (PLATFORM_SUPPORT sees the
 * pages but write actions stay disabled).
 */
export const PLATFORM_ROLES: Role[] = ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'];

/** Roles that grant write access on inside. PLATFORM_SUPPORT is intentionally excluded. */
export const PLATFORM_ADMIN_ROLES: Role[] = ['PLATFORM_ADMIN'];

// ─────────────────────────────────────────────────────────────────────────────
// Legacy bundle aliases — kept as exports so existing `*libHasRole="SITE_ROLES"`
// callsites don't have to be rewritten in this pass. Each one now resolves to the
// canonical PLATFORM_ROLES set: anyone allowed inside the app (PLATFORM_ADMIN or
// PLATFORM_SUPPORT) can see the gated UI; the per-button `isXxxAdmin` signals
// gate writes.
// ─────────────────────────────────────────────────────────────────────────────
export const GATEWAY_ROLES: Role[] = PLATFORM_ROLES;
export const GATEWAY_ADMIN: Role[] = PLATFORM_ADMIN_ROLES;
export const DEVICE_ROLES: Role[] = PLATFORM_ROLES;
export const DEVICE_ADMIN: Role[] = PLATFORM_ADMIN_ROLES;
export const SITE_ROLES: Role[] = PLATFORM_ROLES;
export const USER_ROLES: Role[] = PLATFORM_ROLES;
export const USER_ADMIN: Role[] = PLATFORM_ADMIN_ROLES;
export const CUSTOMER_ROLES: Role[] = PLATFORM_ROLES;
